import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import { LumiMascot } from "../../components/Brand";
import api, { getToken, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Send, Mic, StopCircle, Volume2, HelpCircle, BookOpen, Lightbulb,
  ImageIcon, X,
} from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { key: "hint",    icon: Lightbulb },
  { key: "explain", icon: BookOpen },
  { key: "concept", icon: HelpCircle },
  { key: "example", icon: BookOpen },
  { key: "quiz",    icon: HelpCircle },
  { key: "check",   icon: HelpCircle },
  { key: "why",     icon: HelpCircle },
  { key: "read_aloud", icon: Volume2 },
];

/**
 * OpenAI TTS via /api/voice/tts. Falls back silently to browser TTS on error.
 * Cache last-played URL to allow re-play without a new API call.
 */
async function speakOpenAI(text, lang) {
  if (!text) return;
  try {
    const csrfMatch = document.cookie.match(/(?:^|;\s*)hb_csrf=([^;]+)/);
    const headers = { "Content-Type": "application/json" };
    if (csrfMatch) headers["X-CSRF-Token"] = decodeURIComponent(csrfMatch[1]);
    const resp = await fetch(`${API}/voice/tts`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ text, voice: "alloy", speed: 1.0 }),
    });
    if (!resp.ok) throw new Error("TTS request failed");
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.play();
    audio.onended = () => URL.revokeObjectURL(url);
  } catch (e) {
    // Fallback to browser Web Speech
    if ("speechSynthesis" in window) {
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 0.95;
      if (lang) utter.lang = lang;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    }
  }
}

function ChatBubble({ role, text, streaming, lang, tReadAloud }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} my-2`}>
      {!isUser && <div className="mr-2 shrink-0"><LumiMascot size={36} /></div>}
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-900"
      } ${streaming ? "stream-caret" : ""}`}>
        {text}
        {!isUser && !streaming && (
          <button className="ml-2 text-[11px] text-blue-700" onClick={() => speakOpenAI(text, lang)} data-testid="btn-read-aloud">
            <Volume2 className="inline w-3 h-3" /> {tReadAloud}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AskLumi() {
  const { t, i18n } = useTranslation();
  const [sp] = useSearchParams();
  const subject = sp.get("subject") || null;
  const initialMode = sp.get("mode") || "guided";

  const [sessionId, setSessionId] = useState(() => localStorage.getItem("hb_last_session") || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState(initialMode);
  const [streaming, setStreaming] = useState(false);
  const [imageB64, setImageB64] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const bottomRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Load existing messages
  useEffect(() => {
    async function boot() {
      if (!sessionId) {
        const r = await api.post("/tutor/sessions");
        setSessionId(r.data.id);
        localStorage.setItem("hb_last_session", r.data.id);
        setMessages([]);
      } else {
        try {
          const r = await api.get(`/tutor/sessions/${sessionId}/messages`);
          setMessages(r.data.messages || []);
        } catch (err) {
          console.warn("Session missing; starting a fresh one:", err);
          const r = await api.post("/tutor/sessions");
          setSessionId(r.data.id);
          localStorage.setItem("hb_last_session", r.data.id);
        }
      }
    }
    boot();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  async function send(text, opts = {}) {
    if (!text && !imageB64) return;
    if (!sessionId) return;
    setStreaming(true);
    const userTurn = { id: Math.random().toString(36).slice(2), role: "user", text: text || "(Please look at this image)", has_image: !!imageB64 };
    setMessages((m) => [...m, userTurn]);

    const assistantTurn = { id: "streaming", role: "assistant", text: "" };
    setMessages((m) => [...m, assistantTurn]);

    const body = {
      session_id: sessionId,
      text,
      subject,
      mode: opts.mode || mode,
      image_base64: imageB64 || null,
    };
    setInput("");
    setImageB64(null);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/message`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(document.cookie.match(/hb_csrf=([^;]+)/) ? { "X-CSRF-Token": decodeURIComponent(document.cookie.match(/hb_csrf=([^;]+)/)[1]) } : {}),
          ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const j = JSON.parse(line.slice(5).trim());
            if (j.delta) {
              full += j.delta;
              setMessages((m) => m.map((x) => x.id === "streaming" ? { ...x, text: full } : x));
            } else if (j.error) {
              toast.error(j.error);
            } else if (j.done) {
              setMessages((m) => m.map((x) => x.id === "streaming" ? { ...x, id: j.turn_id } : x));
            }
          } catch (err) {
            // Malformed/partial SSE chunk — ignore this line but keep streaming.
            console.warn("SSE parse skipped", err);
          }
        }
      }
    } catch (e) {
      toast.error(t("ask.troubleReplying"));
      setMessages((m) => m.filter((x) => x.id !== "streaming"));
    } finally {
      setStreaming(false);
    }
  }

  function onImage(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setImageB64(reader.result);
    reader.readAsDataURL(f);
  }

  async function startRecord() {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(t("ask.micNotSupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("language", i18n.resolvedLanguage || "en");
          const csrfMatch = document.cookie.match(/(?:^|;\s*)hb_csrf=([^;]+)/);
          const headers = {};
          if (csrfMatch) headers["X-CSRF-Token"] = decodeURIComponent(csrfMatch[1]);
          const resp = await fetch(`${API}/voice/stt`, {
            method: "POST",
            credentials: "include",
            headers,
            body: fd,
          });
          if (!resp.ok) throw new Error(await resp.text());
          const j = await resp.json();
          if (j.text) setInput((v) => (v ? v + " " : "") + j.text);
        } catch (err) {
          toast.error(t("ask.sttFailed"));
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error(t("ask.micDenied"));
    }
  }

  function stopRecord() {
    try {
      recorderRef.current?.stop();
    } catch (err) {
      console.warn("MediaRecorder stop failed:", err);
    }
    setRecording(false);
  }

  return (
    <AppShell isStudent>
      <div className="grid lg:grid-cols-12 gap-4">
        <aside className="lg:col-span-3 space-y-3">
          <div className="hb-card p-4">
            <div className="flex items-center gap-3">
              <LumiMascot size={64} className="lumi-bounce" />
              <div>
                <div className="font-student font-bold text-lg text-slate-900">{t("ask.title")}</div>
                <div className="text-xs text-slate-500">
                  {subject ? t("ask.focus", { subject }) : t("ask.anySubject")}
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-600 mt-3">{t("ask.helpsExplain")}</div>
          </div>
          <div className="hb-card p-3">
            <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">{t("ask.quickOptions")}</div>
            <div className="grid gap-1">
              {MODES.map((m) => (
                <button key={m.key}
                        data-testid={`mode-${m.key}`}
                        className={`text-left text-sm rounded-lg px-3 py-2 flex items-center gap-2 transition-colors ${
                          mode === m.key ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-800"
                        }`}
                        onClick={() => { setMode(m.key); send(m.key === "read_aloud" ? "Please read the current question aloud." : `Use mode: ${m.key}. ${input || "Please help with my current problem."}`, { mode: m.key }); }}>
                  <m.icon className="w-4 h-4" /> {t(`ask.modes.${m.key}`)}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="lg:col-span-9 hb-card p-4 flex flex-col" style={{ minHeight: "70vh" }}>
          <div className="flex-1 overflow-y-auto pr-1" data-testid="chat-window">
            {messages.length === 0 && (
              <div className="text-center py-10">
                <LumiMascot size={120} className="mx-auto lumi-bounce" />
                <div className="font-student font-bold text-2xl text-slate-900 mt-3">{t("ask.empty")}</div>
                <div className="text-slate-600 text-sm">{t("ask.emptyBody")}</div>
              </div>
            )}
            {messages.map((m) => (
              <ChatBubble key={m.id} role={m.role} text={m.text} streaming={m.id === "streaming" && streaming} lang={i18n.resolvedLanguage} tReadAloud={t("common.readAloud")} />
            ))}
            <div ref={bottomRef} />
          </div>

          {imageB64 && (
            <div className="mt-2 relative w-fit">
              <img src={imageB64} alt="attached" className="max-h-32 rounded-lg border" />
              <button onClick={() => setImageB64(null)} className="absolute -top-2 -right-2 bg-slate-900 text-white rounded-full p-1"><X className="w-3 h-3"/></button>
            </div>
          )}

          <div className="mt-3 flex items-end gap-2 border-t border-slate-200 pt-3">
            <label className="p-2 rounded-lg hover:bg-slate-100 cursor-pointer" title={t("common.open")} data-testid="btn-attach-image">
              <ImageIcon className="w-5 h-5 text-slate-700" />
              <input type="file" accept="image/*" className="hidden" onChange={onImage} />
            </label>
            <button className={`p-2 rounded-lg hover:bg-slate-100`}
                    onClick={() => (recording ? stopRecord() : startRecord())}
                    title={t("common.readAloud")} data-testid="btn-voice-input"
                    disabled={transcribing}>
              {(() => {
                if (recording) return <StopCircle className="w-5 h-5 text-red-600" />;
                if (transcribing) return <Mic className="w-5 h-5 text-blue-600 animate-pulse" />;
                return <Mic className="w-5 h-5 text-slate-700" />;
              })()}
            </button>
            <Textarea rows={2}
                      placeholder={t("ask.inputPlaceholder")}
                      value={input}
                      data-testid="chat-input"
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }}}
                      className="min-h-[46px] max-h-40" />
            <Button onClick={() => send(input)} disabled={streaming || (!input && !imageB64)}
                    data-testid="chat-send"
                    aria-label={t("ask.sendMessage")}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-6">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
